import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const PrivacyPolicy = () => {
  return (
    <>
      <SEO 
        title="Privacy Policy - BlogCraft"
        description="BlogCraft's Privacy Policy - Learn how we collect, use, and protect your personal information."
        keywords="privacy policy, data protection, user privacy, blogcraft privacy"
      />

      <div className="bg-gradient-to-r from-blue-600 to-purple-600 py-12">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Privacy Policy</h1>
          <p className="text-blue-100">Last updated: January 3, 2026</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="prose prose-lg max-w-none">
              
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Introduction</h2>
                <p className="text-gray-700 mb-4">
                  At BlogCraft, we are committed to protecting your privacy and ensuring the security of your personal information. 
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website 
                  blogcraft.shop and use our services.
                </p>
                <p className="text-gray-700">
                  By using BlogCraft, you consent to the practices described in this policy. If you do not agree with the terms 
                  of this privacy policy, please do not access or use our website.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Information We Collect</h2>
                
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Information You Provide</h3>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Name and email address when subscribing to our newsletter</li>
                  <li>Contact information when you reach out to us via our contact form</li>
                  <li>Comments and feedback you provide on our articles</li>
                  <li>Account information if you create an account on our platform</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">Information Automatically Collected</h3>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>IP address and browser information</li>
                  <li>Device information and operating system</li>
                  <li>Pages visited and time spent on our website</li>
                  <li>Referring website and search terms used</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">How We Use Your Information</h2>
                <p className="text-gray-700 mb-4">We use your information for the following purposes:</p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>To provide and maintain our website and services</li>
                  <li>To send you newsletters and marketing communications (with your consent)</li>
                  <li>To respond to your inquiries and provide customer support</li>
                  <li>To improve our website and user experience</li>
                  <li>To analyze website usage and trends</li>
                  <li>To detect and prevent fraud, abuse, and security issues</li>
                  <li>To comply with legal obligations</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Cookies and Tracking Technologies</h2>
                <p className="text-gray-700 mb-4">
                  We use cookies and similar tracking technologies to track activity on our website and hold certain information. 
                  Cookies are files with small amount of data which may include an anonymous unique identifier.
                </p>
                <p className="text-gray-700 mb-4">
                  You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, 
                  if you do not accept cookies, you may not be able to use some portions of our website.
                </p>
                <p className="text-gray-700">
                  We use cookies for the following purposes:
                </p>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>Essential cookies required for website functionality</li>
                  <li>Analytics cookies to understand how our website is used</li>
                  <li>Marketing cookies to personalize your experience</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Services</h2>
                <p className="text-gray-700 mb-4">
                  Our website may contain links to third-party websites and services. We are not responsible for the privacy 
                  practices of these third-party services. We encourage you to review the privacy policies of any third-party 
                  services you visit.
                </p>
                <p className="text-gray-700">
                  We use the following third-party services:
                </p>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>Google Analytics for website analytics</li>
                  <li>Email marketing services for newsletter distribution</li>
                  <li>Social media platforms for content sharing</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Security</h2>
                <p className="text-gray-700 mb-4">
                  We implement appropriate technical and organizational measures to protect your personal information against 
                  unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet 
                  or method of electronic storage is 100% secure.
                </p>
                <p className="text-gray-700">
                  We regularly review our security practices and update them as necessary to ensure the ongoing protection of your data.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Rights</h2>
                <p className="text-gray-700 mb-4">
                  Depending on your location, you may have the following rights regarding your personal information:
                </p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Access to your personal information</li>
                  <li>Correction of inaccurate information</li>
                  <li>Deletion of your personal information</li>
                  <li>Restriction of processing</li>
                  <li>Data portability</li>
                  <li>Objection to processing</li>
                </ul>
                <p className="text-gray-700">
                  To exercise these rights, please contact us using the information provided below.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Children's Privacy</h2>
                <p className="text-gray-700">
                  Our website is not intended for children under the age of 13. We do not knowingly collect personally 
                  identifiable information from children under 13. If you are a parent or guardian and you are aware that 
                  your child has provided us with personal information, please contact us.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to This Privacy Policy</h2>
                <p className="text-gray-700">
                  We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new 
                  Privacy Policy on this page and updating the "Last updated" date at the top of this policy.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
                <p className="text-gray-700 mb-4">
                  If you have any questions about this Privacy Policy, please contact us:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700"><strong>Website:</strong> <Link to="/contact" className="text-blue-600 hover:underline">Contact Form</Link></p>
                  <p className="text-gray-700"><strong>Response Time:</strong> We will respond to your inquiry within 7 business days.</p>
                </div>
              </section>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
